import { Type, type Static } from '@sinclair/typebox';
import type { ActivityUiAction } from './activity-action';
import type { CommentMention } from '@ayocore/comments/common/types/comment-mention';
import type { CommentContactMention } from '@ayocore/comments/common/types/comment-contact-mention';

// ─── Атомарные схемы ────────────────────────────────────────────────────────

/** Файл-вложение комментария */
export const ActivityFileAttachmentSchema = Type.Object({
  id: Type.Integer(),
  uuid: Type.String(),
  original_name: Type.Union([Type.String(), Type.Null()]),
  file_size: Type.Union([Type.Integer(), Type.Null()]),
  mime_type: Type.Union([Type.String(), Type.Null()]),
  public_url: Type.Union([Type.String(), Type.Null()]),
});

export type ActivityFileAttachment = Static<typeof ActivityFileAttachmentSchema>;

/** Схема mention внутри комментария (offset-based) */
export const ActivityMentionSchema = Type.Object({
  user_id: Type.Integer(),
  label: Type.String(),
  start: Type.Integer(),
  end: Type.Integer(),
});

/** Данные комментария для inline-доставки через WS */
export const ActivityCommentPayloadSchema = Type.Object({
  uuid: Type.String(),
  author_id: Type.Integer(),
  author_name: Type.String(),
  avatar_url: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  content: Type.String(),
  created_at: Type.String(),
  mentions: Type.Optional(Type.Array(ActivityMentionSchema)),
  files: Type.Optional(Type.Array(ActivityFileAttachmentSchema)),
});

/** TS-тип данных комментария в WS payload */
export type ActivityCommentPayload = Static<typeof ActivityCommentPayloadSchema>;

// ─── WS channel message ────────────────────────────────────────────────────

/**
 * Realtime channel message for `crm-activity` websocket updates.
 * This message signals that a thread changed and clients should reload.
 */
export const ActivityChannelMessageSchema = Type.Object(
  {
    type: Type.Literal('thread_changed'),
    thread_id: Type.Integer(),
    event_id: Type.Union([Type.Integer(), Type.Null()]),
    event_type: Type.String(),
    entity_type: Type.String(),
    entity_uuid: Type.String(),
    occurred_at: Type.String({ format: 'date-time' }),
    reason: Type.Optional(Type.String()),
    comment_uuid: Type.Optional(Type.String({ format: 'uuid' })),
    /** Данные комментария для comment_added/comment_updated — позволяет клиенту отобразить сообщение без HTTP-запроса */
    comment: Type.Optional(ActivityCommentPayloadSchema),
  },
  { $id: 'ActivityChannelMessage' }
);

export type ActivityChannelMessage = Static<typeof ActivityChannelMessageSchema>;

// ─── Chat item ──────────────────────────────────────────────────────────────

/**
 * Unified chat/timeline item used by CRM activity UI.
 * Both user comments and system events are normalized to this shape.
 */
export type ActivityChatItem = {
  id: string;
  thread_id: number;
  kind: 'comment' | 'system';
  is_own?: boolean;
  comment_uuid?: string;
  author_id?: number | null;
  avatar_url?: string | null;
  content: string;
  author_name: string;
  created_at: Date | string | null;
  edited?: boolean;
  deleted?: boolean;
  event_type?: string;
  entity_type?: string;
  entity_uuid?: string;
  scope?: {
    entity_type: string;
    entity_uuid: string;
    label?: string;
  };
  actions?: ActivityUiAction[];
  mentions?: CommentMention[];
  contact_mentions?: CommentContactMention[];
  files?: ActivityFileAttachment[];
  replyToMessage?: {
    commentUuid?: string;
    preview: string;
    authorId?: number | null;
    authorName?: string | null;
  };
};
